import { isNil, map, pipe, prepend, without } from 'ramda';
import React, { FC, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { Avatar, Popover, Text } from '@wix/design-system';
import { ContextMenuItem } from '../../../../../../components/common/ContextMenu/ContextMenuItem';
import { Edit } from '../../../../../../components/common/WixIcons';
import { ModalName } from '../../../../../../constants/ModalName';
import {
  TrackBoardActionsMenuClickOrigin,
  TrackBoardActionsMenuTarget,
  useTrackBoardAnalytics,
} from '../../../../../../utils/hooks/bi/useTrackBoardAnalytics';
import { useBoolean } from '../../../../../../utils/hooks/useBoolean';
import { useModalHandlersAndOptions } from '../../../../../../utils/hooks/useModal';
import { MediaManagerSDKTeamMember } from '../../../../../../utils/media-manager-sdk';
import { findOwner, isOwner } from '../utils';
import styles from './TeammatesMenu.scss';
import { IOuter } from './TeammatesMenu.types';

export const TeammatesMenu: FC<IOuter> = ({ team, board, children, showManageButton }) => {
  const { value: isPopoverActive, setFalse: closePopover, toggle: togglePopover } = useBoolean();

  const owner = findOwner(team);
  const sortedTeam = isNil(owner)
    ? team.members
    : pipe<MediaManagerSDKTeamMember[], MediaManagerSDKTeamMember[], MediaManagerSDKTeamMember[]>(
        without([owner]),
        prepend(owner),
      )(team.members);
  const { openModal: openEditBoardModal } = useModalHandlersAndOptions(ModalName.EditBoard);
  const { trackBoardActionsClick } = useTrackBoardAnalytics();

  const handleManageBoardClick = useCallback(() => {
    openEditBoardModal({ board });

    trackBoardActionsClick([board], {
      target: TrackBoardActionsMenuTarget.Manage,
      clickOrigin: TrackBoardActionsMenuClickOrigin.TeammatesList,
    });
  }, [board, openEditBoardModal, trackBoardActionsClick]);

  const teamElement = (
    <div className={styles.menuTeam}>
      {map(
        (teamMember) => (
          <div key={teamMember.userId} className={styles.menuTeammate}>
            <Avatar
              size="size36"
              name={`${teamMember.firstName} ${teamMember.lastName}`}
              imgProps={{ src: teamMember.img }}
              className={styles.menuTeammateAvatar}
            />
            <div className={styles.menuTeammateInfo}>
              <Text size="small" weight="normal" tagName="div">
                <FormattedMessage
                  id="boards.teams.full-name"
                  values={{
                    firstName: teamMember.firstName,
                    lastName: teamMember.lastName,
                  }}
                />
              </Text>
              {isOwner(teamMember) && (
                <Text size="tiny" weight="normal" tagName="div" className={styles.menuTeammateOwnerLabel}>
                  <FormattedMessage id="boards.teams.owner" />
                </Text>
              )}
            </div>
          </div>
        ),
        sortedTeam,
      )}
    </div>
  );

  return (
    <Popover
      placement="top"
      shown={isPopoverActive}
      animate
      showArrow
      onClickOutside={closePopover}
      moveBy={{ y: 15 }}
      appendTo="viewport"
      zIndex={10000}
      dataHook="manage-team-popover"
    >
      <Popover.Element>
        <div onClick={togglePopover} data-hook="manage-team-popover-toggle">
          {children}
        </div>
      </Popover.Element>
      <Popover.Content>
        <div className={styles.teamPopover} data-hook="team-menu-content">
          <ContextMenuItem
            title={<FormattedMessage id="boards.teams.your-teammates" />}
            titleTextSize="medium"
            titleTextWeight="bold"
            menuItemClassName={styles.yourTeammatesTitle}
            ellipsis
          />

          {teamElement}

          {showManageButton && (
            <ContextMenuItem
              icon={<Edit />}
              iconClassName={styles.manageBoard}
              title={<FormattedMessage id="boards.teams.manage-board" />}
              titleTextSize="medium"
              titleClassName={styles.manageBoard}
              onClick={handleManageBoardClick}
              menuItemClassName={styles.manageBoardTitle}
              dataHook="manage-board-button"
              ellipsis
            />
          )}
        </div>
      </Popover.Content>
    </Popover>
  );
};
